import { FC, useState } from "react";
import { ClientsManageResources, useClientManageContext } from "./ClientManageContext";
import { ListContextProvider } from "ra-core";
import { TabContext, TabPanel } from "@mui/lab";
import { Tabs, Tab, Box, IconButton, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import Scrollbar from "~/components/Scrollbar";
import { ClientManageDto, ClientStatus } from "~/gen/api/user";
import ClientsTable from "./ClientsTable";
import { Pagination as DefaultPagination } from '~/components/list/pagination'
import { useHasPermit } from "~/authProvider/useHasPermit";
import { FunctionField } from "~/components/field";
import { MANAGE_ONDATA_CLIENTS } from "~/constants";
import { RecPatch } from "~/dataProvider/tools";
import CreateOrEdit from "~/controller/CreateOrEdit";
import EditClient from "./EditClient";


const { clientsSecurefileRes } = ClientsManageResources

const SecurefileAppsList: FC = () => {
  const [states, setStates] = useState(() => ({
    toRevoke: null as ClientManageDto|null,
    toResume: null as ClientManageDto|null,
    toViewDetail: null as ClientManageDto|null,
  }))
  
  const { listCtrls: {securefile: clientsSecurefileCtrl}, states:{ currTab }, setCurrTab } = useClientManageContext();
  const { setPage, setSort, setFilters, refetch, displayedFilters, filterValues } = clientsSecurefileCtrl

  const hasWritePermit = useHasPermit([MANAGE_ONDATA_CLIENTS])
  
  const handleTabsChange = (event: any, newValue: string) => {
    setFilters({...filterValues, status: newValue}, displayedFilters, false)
    setPage(1)
    setSort(
      newValue === (ClientStatus[ClientStatus.PendingApproval]) ? 'register_at'
      : newValue === (ClientStatus[ClientStatus.Enabled])  ? 'reviewed_at'
      : newValue === (ClientStatus[ClientStatus.Rejected]) ? 'reviewed_at'
      : newValue === (ClientStatus[ClientStatus.Suspend])  ? 'suspended_at'
      : newValue === (ClientStatus[ClientStatus.Revoked])  ? 'revoked_at'
      : 'id',
      'DESC'
    )
    setCurrTab(newValue)
  };

  return (
    <>
      <ListContextProvider value={clientsSecurefileCtrl}>
        <TabContext value={currTab}>
          <Tabs
            indicatorColor="secondary"
            onChange={handleTabsChange}
            scrollButtons="auto"
            textColor="secondary"
            value={currTab}
            variant="scrollable"
          >
            <Tab label="Active" value={ClientStatus[ClientStatus.Enabled]} />
            <Tab label="Suspended" value={ClientStatus[ClientStatus.Suspend]} />
            <Tab label="Revoked" value={ClientStatus[ClientStatus.Revoked]} />
          </Tabs>
          
          <Scrollbar>
            <Box sx={{ minWidth: 1150, '& .MuiTabPanel-root': { p: 'unset' } }}>
              <TabPanel value={ClientStatus[ClientStatus.Enabled]} >
                <ClientsTable
                  ActionField={
                    hasWritePermit &&
                    <FunctionField label="Actions" sortable={false}
                      render={(d?: ClientManageDto & RecPatch) => (
                        <>
                          <Tooltip title="Edit">
                            <IconButton 
                              aria-label="Edit Client"
                              onClick={(e:any) => { setStates(v => ({...v, toRevoke: d || null})) }}
                              sx={{ 
                                color: 'primary.main' as any, 
                                '&:hover': { backgroundColor: 'action.hover' },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    />
                  }
                />
              </TabPanel>
              <TabPanel value={ClientStatus[ClientStatus.Suspend]} >
                <ClientsTable includeSuspendTime={true}
                  ActionField={
                    hasWritePermit &&
                    <FunctionField label="Actions" sortable={false}
                      render={(d?: ClientManageDto & RecPatch) => (
                        <Tooltip title="Edit">
                          <IconButton 
                            aria-label="Edit Client"
                            onClick={(e:any) => { setStates(v => ({...v, toResume: d || null})) }}
                            sx={{ 
                              color: 'primary.main' as any, 
                              '&:hover': { backgroundColor: 'action.hover' },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    />
                  }
                />
              </TabPanel>
              <TabPanel value={ClientStatus[ClientStatus.Revoked]} >
                <ClientsTable includeRevokeTime={true} 
                  selectRow={(x:any) => { hasWritePermit && setStates(v => ({...v, toViewDetail: x}))}}
                />
              </TabPanel>
            </Box>
          </Scrollbar>
          {//@ts-ignore 
          <DefaultPagination {...clientsSecurefileCtrl} />}
        </TabContext>
      </ListContextProvider>

      <CreateOrEdit resource={clientsSecurefileRes} basePath="" mutationMode="pessimistic" 
        id={(states.toRevoke || states.toResume)?.clientId || undefined}  
        record={states.toRevoke || states.toResume || states.toViewDetail}
        onSuccess={refetch}
      >
        {hasWritePermit &&
          <EditClient 
            open={!!states.toRevoke || !!states.toResume || !!states.toViewDetail} 
            onClose={()=>{ setStates(v => ({...v, toRevoke: null, toResume: null, toViewDetail: null })) }} 
          />}
      </CreateOrEdit>
    </>
  )
}

export default SecurefileAppsList;
